import last from 'lodash/last';
import { FormGroup } from '@angular/forms';
import { Directive, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DeliveryCalendar, UiDropdownComponent } from '@cakeiteasy/web-ui';
import {
  CalendarSchedule,
  CustomComponent,
  IDeliveryOrder,
  IDropdownItem,
  PickupCalendarDay,
  ToDoorCalendarDay
} from '@cakeiteasy/web-utils';
import {
  ICartDailyStockSoldOutProductType,
  ICartDeliveryInfoProductType,
} from '../cart/models/delivery';
import { IOverwritingDelivery } from '../cart';
import { IDeliveryAddress } from '../users';

@Directive({ standalone: false })
export abstract class DeliveryFormBase extends CustomComponent {
  @Input() bakeryId: number;
  @Input() orderId: number;
  @Input() currency = 'kr';
  @Input() countryCode: string;
  @Input() deliveryOrder: IDeliveryOrder;
  @Input() deliveryAddresses: IDeliveryAddress[] = null;
  @Input() showDeliveryPricesInfo = true;
  @Input() deliveryNoteForCustomer: string;
  @Input() fullCapacityDays: { begin: string, end: string }[];
  @Input() availableProductTypes: ICartDeliveryInfoProductType[];
  @Input() dailyStockSoldOutProductTypes: ICartDailyStockSoldOutProductType[] = [];
  @Input() overwritingDelivery: IOverwritingDelivery;
  @Input() possibleToRegister: boolean = true;
  @Input() isAnotherDeliveryAvailable: boolean;
  @Input() nextBtnTextKey = 'orderProcess.delivery.goToPayment';
  @Input() nextBtnView = 'btn-pink';

  /**Unconfirmed form values from a previous instance of this component, so switching delivery
   * type away and back (which destroys/recreates this component) doesn't lose what the user typed.*/
  @Input() draft: Partial<IDeliveryOrder> | null = null;
  @Output() draftChange = new EventEmitter<Partial<IDeliveryOrder>>();

  @Output() onNext = new EventEmitter<{ deliveryOrder: IDeliveryOrder, saveCurrentDelivery?: boolean }>();
  @Output() onRegister = new EventEmitter<void>();
  @Output() onChooseAnotherDelivery = new EventEmitter<void>();

  @ViewChild('rangeDropDown', { static: false }) rangeDropDown: UiDropdownComponent;
  @ViewChild('datePickerLabel', { static: false }) datePickerLabel: ElementRef<HTMLLabelElement>;

  dp: any;
  schedule: CalendarSchedule[] | string[] = [];
  saveAddressOnSubmit: boolean;

  busy: Promise<any>;

  deliveryTimeItems: IDropdownItem[];

  PHONE_PATTERN = /^\+?[\d\s\(\)-]+$/;
  STATUS = { VALID: 'VALID', INVALID: 'INVALID', MISSING_STREET_NUMBER: 'MISSING_STREET_NUMBER' };

  price: number | null = null;
  isPriceSet = false;
  deliveryForm: FormGroup;

  deliveryNote = '';

  protected constructor() {
    super();
  }

  abstract setPrice(params?: any): void;

  abstract receiveScheduleFromDay(calendarDay: ToDoorCalendarDay | PickupCalendarDay): Promise<CalendarSchedule[] | string[]>;

  abstract mapScheduleToTimeItems(schedule: CalendarSchedule[] | string[]): IDropdownItem[];

  abstract findSchedule(schedule: CalendarSchedule[] | string[], timeRange: string): CalendarSchedule | string;

  abstract calendarFetcher(fromDate: Date, prevCalendar?: DeliveryCalendar): Promise<DeliveryCalendar>;

  abstract confirmDelivery(): void;

  abstract timeChanged(event: IDropdownItem): void;

  isCurrentAddressNotSaved() {
    const { deliveryPostNumber, deliveryAddress } = this.deliveryForm.getRawValue();

    return !this.deliveryAddresses.find(({ postNumber, address }) => {
      return deliveryPostNumber.toLowerCase().trim() === postNumber.toLowerCase().trim()
        && deliveryAddress.toLowerCase().trim() === address.toLowerCase().trim();
    });
  }

  isNextBtnDisabled() {
    return this.deliveryForm && this.deliveryForm.invalid;
  }

  isDateTimeValid() {
    /**
     * It’s more correct to use the valid property, but if the delivery date is expired,
     * a strange bug occurs — so it’s more reliable to use Boolean(value).
     * Steps to reproduce the bug:
     * - The delivery date must be earlier than today.
     * - When initializing the form, don’t change the date or touch the datepicker.
     * - Enter a new address.
     * - Select a delivery date.
     * Result: deliveryDate.valid = false but should be true.
     * */
    return this.deliveryForm
      // && this.deliveryForm.get('deliveryDate').valid
      && Boolean(this.deliveryForm.get('deliveryDate').value)
      && this.deliveryForm.get('deliveryTime').valid;
  }

  getTimeValue(): string {
    return this.deliveryForm.get('deliveryTime').value;
  }

  getDeliveryNote(): string {
    return '';
  }

  /**The draft (unconfirmed values typed before this component was last destroyed) takes
   * priority over the last confirmed delivery order, which only reflects a completed "Next" click.*/
  getInitialFormValue(expectedType: string): Partial<IDeliveryOrder> | null {
    if (this.draft) {
      return this.draft;
    }

    return this.deliveryOrder?.type === expectedType ? this.deliveryOrder : null;
  }

  protected _watchDraft(): void {
    this.addSubscriber(this.deliveryForm.valueChanges
      .subscribe((value) => this.draftChange.emit(value)));
  }

  getInitialAddress(deliveryAddresses: IDeliveryAddress[]): IDeliveryAddress | null {
    const isDeliveryExist = this.draft?.deliveryPostNumber || this.deliveryOrder?.deliveryPostNumber;

    if (!deliveryAddresses || deliveryAddresses.length === 0 || isDeliveryExist) {
      return null;
    }

    return deliveryAddresses.find((address) => address.isFavourite)
      || deliveryAddresses[0];
  }

  async scheduleChange(calendarDay: ToDoorCalendarDay) {
    const { deliveryAgentId } = last(calendarDay.variants);
    this.deliveryForm.get('deliveryAgent').patchValue(deliveryAgentId);

    const schedule = await this.changeScheduleFlow(calendarDay);

    if (!schedule) {
      return;
    }

    const selectedSchedule = this.findSchedule(schedule as CalendarSchedule[], this.getTimeValue()) as CalendarSchedule;

    if (selectedSchedule) {
      this.setPrice((selectedSchedule).price);
    } else {
      setTimeout(() => this.rangeDropDown.open(), 100);
    }
  }

  async changeScheduleFlow(calendarDay: ToDoorCalendarDay | PickupCalendarDay): Promise<CalendarSchedule[] | string[]> {
    const schedule = await this.receiveScheduleFromDay(calendarDay);
    const deliveryTimeControl = this.deliveryForm.get('deliveryTime');
    this.schedule = schedule;
    this.deliveryTimeItems = schedule ? this.mapScheduleToTimeItems(schedule) : null;

    if (schedule) {
      deliveryTimeControl.enable();
    } else {
      deliveryTimeControl.patchValue(null);
      deliveryTimeControl.disable();
    }

    return schedule;
  }

  protected patchDeliveryForm(deliveryOrder: Partial<IDeliveryOrder>) {
    this.deliveryForm.patchValue(deliveryOrder);
  }
}
